import { GUARDIAN_COLORS, POI_RULE, POI_RULES, SIMPATIA_CHESTS, type Anchor, type Mode, type PoiType } from "@eldrun/content";
import { datan2, dcos, deriveSeed, dsin, hash32, Rng, toUnitFloat } from "@eldrun/shared";
import { BIOME } from "./biomes.ts";
import { PLAN_SIDE, PLAN_SPACING, WORLD_HALF } from "./params.ts";
import type { TerrainField } from "./terrain.ts";

/** Um ponto de interesse do descritor do mundo (doc 04): igual no servidor e no cliente. */
export interface Poi {
  id: number;
  type: PoiType;
  x: number;
  z: number;
  /** Giro (rad): o "+z local" do layout aponta para (sin, cos). */
  rot: number;
  /** Carimbo: raio plano, raio da transição e altura-alvo. */
  inner: number;
  outer: number;
  target: number;
  /** Guardião: índice da cor; Pedra de Encanto: 0 desafio, 1 chefe; Baú de Simpatia: índice do tipo. */
  data: number;
}

/** Grades do plano que o planejador lê (8 m). */
export interface PoiGrids {
  seed: number;
  mode: Mode;
  heights: Float32Array;
  slope: Float32Array;
  biome: Uint8Array;
  mainLand: Uint8Array;
  coastDistance: Float32Array;
}

export interface PoiPlan {
  pois: Poi[];
  /** Obrigatórios que faltaram (vazio = ilha completa). */
  missing: string[];
  /** Não obrigatórios abaixo do mínimo da regra (a ilha vale, mas outra sub-seed pode completar). */
  short: string[];
  /** Rumo da travessia (do centro de massa para os destroços), normalizado. */
  heading: { x: number; z: number };
}

const TAN = { d6: 0.1051, d8: 0.1405, d14: 0.2493, d20: 0.364, d25: 0.4663, d40: 0.8391 } as const;
const MARGIN = 6;

const cellOf = (v: number) => Math.min(PLAN_SIDE - 1, Math.max(0, Math.round((v + WORLD_HALF) / PLAN_SPACING)));
const coord = (i: number) => -WORLD_HALF + i * PLAN_SPACING;

/** Posição de uma âncora no mundo, com o giro do POI (trigonometria determinística). */
export function poiPoint(poi: { x: number; z: number; rot: number }, anchor: Anchor): { x: number; z: number } {
  const lx = "a" in anchor ? dsin(anchor.a) * anchor.r : anchor.x;
  const lz = "a" in anchor ? dcos(anchor.a) * anchor.r : anchor.z;
  const c = dcos(poi.rot);
  const s = dsin(poi.rot);
  return { x: poi.x + lx * c + lz * s, z: poi.z - lx * s + lz * c };
}

/**
 * Planejador global de POIs (doc 04, POIs: ordem e regras): do mais restrito ao mais flexível,
 * cada etapa gera candidatos com o stream h(seed, tipo, sub), aplica os filtros rígidos e escolhe
 * o de maior pontuação (best-candidate), respeitando distâncias por par e a pegada reservada de
 * cada POI. Na Treta, sem destroços, Boca, chegada e guardiões, com 6 pontos de nascimento.
 * `sub` é a sub-seed das novas tentativas quando falta um obrigatório.
 */
export function planPois(grids: PoiGrids, field: TerrainField, radius: number, sub = 0): PoiPlan {
  const g = new Grids(grids);
  const pois: Poi[] = [];
  const missing: string[] = [];
  const treta = grids.mode === "treta";
  const centroid = g.centroid();
  let heading = { x: 1, z: 0 };
  const rng = (type: PoiType, k = 0) => new Rng(deriveSeed(grids.seed, `poi-${type}`, sub, k));
  const add = (type: PoiType, x: number, z: number, rot: number, data = 0): Poi => {
    const rule = POI_RULE.get(type)!;
    const poi: Poi = { id: pois.length, type, x, z, rot, inner: rule.inner, outer: rule.outer, target: rule.inner > 0 ? stampTarget(field, x, z, rule.inner) : 0, data };
    pois.push(poi);
    return poi;
  };
  const dist = (a: { x: number; z: number }, b: { x: number; z: number }) => Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
  /** Fora da pegada dos POIs já postos (com folga). */
  const clear = (x: number, z: number, footprint: number) => pois.every((p) => p.type === "boca" || dist(p, { x, z }) >= footprintOf(p.type) + footprint + MARGIN);
  const of = (type: PoiType) => pois.filter((p) => p.type === type);
  const count = (type: PoiType): number => {
    const rule = POI_RULE.get(type)!;
    const [min, max] = treta ? (rule.treta ?? [0, 0]) : rule.count;
    return min + Math.floor(toUnitFloat(hash32(grids.seed, type.length, sub, 77)) * (max - min + 1));
  };
  const minOf = (type: PoiType): number => {
    const rule = POI_RULE.get(type)!;
    return (treta ? (rule.treta ?? [0, 0]) : rule.count)[0];
  };
  const randomLand = (r: Rng): { x: number; z: number } => ({ x: r.range(-radius, radius), z: r.range(-radius, radius) });

  /** Escolhe o melhor de `k` candidatos (ou de uma lista), ou `undefined`. */
  const best = <T extends { x: number; z: number }>(candidates: Iterable<T>, ok: (c: T) => boolean, score: (c: T) => number): T | undefined => {
    let pick: T | undefined;
    let top = -Infinity;
    for (const c of candidates) {
      if (!ok(c)) continue;
      const s = score(c);
      if (s > top) {
        top = s;
        pick = c;
      }
    }
    return pick;
  };
  const draws = function* (r: Rng, k: number) {
    for (let i = 0; i < k; i++) yield randomLand(r);
  };
  const nearest = (c: { x: number; z: number }, list: readonly Poi[]) => list.reduce((m, p) => Math.min(m, dist(p, c)), Infinity);

  if (!treta) {
    // 1. Destroços: praia plana com mar aberto por 400 m na direção de fora, longe do centro de massa.
    const wreck = best(
      g.cells((i) => g.biome[i] === BIOME.praia && g.mainLand[i] === 1),
      (c) => g.flat(c.x, c.z, 25, TAN.d8, 0.4) && g.openSea(c.x, c.z, centroid, 400),
      (c) => dist(c, centroid) + toUnitFloat(hash32(grids.seed, c.i, sub)) * 20,
    );
    if (wreck) {
      const d = dist(wreck, centroid) || 1;
      heading = { x: (wreck.x - centroid.x) / d, z: (wreck.z - centroid.z) / d };
      add("destrocos", wreck.x, wreck.z, datan2(heading.x, heading.z));
      // 2. Boca do Boitatá: no mar fundo, a 300–450 m dos destroços no rumo da travessia.
      const options: { x: number; z: number; score: number }[] = [];
      for (let d2 = 300; d2 <= 450; d2 += 15) {
        for (const off of [0, 0.15, -0.15, 0.3, -0.3]) {
          const a = datan2(heading.x, heading.z) + off;
          const x = wreck.x + dsin(a) * d2;
          const z = wreck.z + dcos(a) * d2;
          options.push({ x, z, score: -Math.abs(d2 - 375) - Math.abs(off) * 100 });
        }
      }
      const mouth = best(options, (c) => Math.abs(c.x) < WORLD_HALF - 20 && Math.abs(c.z) < WORLD_HALF - 20 && g.height(c.x, c.z) < -8, (c) => c.score);
      if (mouth) add("boca", mouth.x, mouth.z, datan2(heading.x, heading.z));
      else missing.push("boca");
    } else missing.push("destrocos", "boca");

    // 3. Chegada: praia ou mata baixa, plana, do lado oposto aos destroços (≥ 0,9 R).
    const wreckPoi = of("destrocos")[0];
    const far = (c: { x: number; z: number }) => (wreckPoi ? dist(c, wreckPoi) : 0);
    const shore = (i: number) => g.mainLand[i] === 1 && (g.biome[i] === BIOME.praia || (g.biome[i] === BIOME.mata && (g.heights[i] as number) < 10));
    let arrival = best(
      g.cells(shore),
      (c) => far(c) >= 0.9 * radius && g.flat(c.x, c.z, 20, TAN.d8, 0.4) && clear(c.x, c.z, footprintOf("chegada")),
      (c) => far(c),
    );
    // Sem praia a 0,9 R, a mais longe que couber (o grupo ainda acorda longe dos destroços).
    arrival ??= best(g.cells(shore), (c) => g.flat(c.x, c.z, 20, TAN.d14, 0.4) && clear(c.x, c.z, footprintOf("chegada")), (c) => far(c));
    if (arrival) add("chegada", arrival.x, arrival.z, datan2(centroid.x - arrival.x, centroid.z - arrival.z));
    else missing.push("chegada");

    // 4. Guardiões: 5 arenas planas de 18 m, ≥ 340 m entre si e ≥ 280 m da chegada, no bioma da cor.
    const arrivalPoi = of("chegada")[0];
    GUARDIAN_COLORS.forEach((color, index) => {
      const r = rng("guardiao", index);
      const prefer = BIOME[color.biome as keyof typeof BIOME];
      const fallback = "fallback" in color ? BIOME[color.fallback as keyof typeof BIOME] : undefined;
      let placed: { x: number; z: number } | undefined;
      for (const spacing of [340]) {
        placed = best(
          draws(r, 900),
          (c) =>
            g.isMainLand(c.x, c.z) &&
            nearest(c, of("guardiao")) >= spacing &&
            (!arrivalPoi || dist(c, arrivalPoi) >= 280) &&
            g.flat(c.x, c.z, 18, TAN.d14, 1) &&
            clear(c.x, c.z, footprintOf("guardiao")),
          (c) => {
            const b = g.biomeAt(c.x, c.z);
            return (b === prefer ? 1000 : b === fallback ? 500 : 0) + Math.min(400, nearest(c, pois));
          },
        );
        if (placed) break;
      }
      if (placed) add("guardiao", placed.x, placed.z, datan2(centroid.x - placed.x, centroid.z - placed.z), index);
      else missing.push(`guardiao-${color.color}`);
    });
  } else {
    // Treta: 6 pontos de nascimento ≥ 250 m entre si e ≥ 120 m da costa.
    const r = rng("nascimento");
    for (let n = 0; n < count("nascimento"); n++) {
      const placed = best(
        draws(r, 900),
        (c) => g.isMainLand(c.x, c.z) && g.coast(c.x, c.z) >= 120 && nearest(c, of("nascimento")) >= 250 && g.flat(c.x, c.z, 6, TAN.d14, 1) && clear(c.x, c.z, footprintOf("nascimento")),
        (c) => Math.min(500, nearest(c, of("nascimento"))),
      );
      if (placed) add("nascimento", placed.x, placed.z, datan2(-placed.x, -placed.z));
      else missing.push("nascimento");
    }
  }

  const arrivalPoi = of("chegada")[0];
  const wreckPoi = of("destrocos")[0];

  // 5. Grutas: pé de serra (declive 20–40°), pátio plano de 8 m à frente, boca voltada para baixo.
  {
    const r = rng("gruta");
    const target = count("gruta");
    const caves = function* () {
      for (let i = 0; i < 3000; i++) {
        const p = randomLand(r);
        const down = g.downhill(p.x, p.z);
        yield { x: p.x + down.x * 10, z: p.z + down.z * 10, rot: datan2(down.x, down.z), sx: p.x, sz: p.z };
      }
    };
    for (let n = 0; n < target; n++) {
      let placed: { x: number; z: number; rot: number } | undefined;
      for (const spacing of [250]) {
        placed = best(
          caves(),
          (c) => {
            const s = g.slopeAt(c.sx, c.sz);
            return s >= TAN.d20 && s <= TAN.d40 && g.height(c.sx, c.sz) >= 3 && g.isMainLand(c.x, c.z) && g.flat(c.x, c.z, 8, 0.36, 1) && nearest(c, of("gruta")) >= spacing && clear(c.x, c.z, footprintOf("gruta"));
          },
          (c) => Math.min(600, nearest(c, of("gruta"))),
        );
        if (placed) break;
      }
      if (!placed) break;
      add("gruta", placed.x, placed.z, placed.rot);
    }
  }

  // 6. Acampamentos do Mascate: mata ou cerrado, plano em 22 m, longe da costa, 200–500 m da chegada.
  {
    const r = rng("acampamento");
    const target = count("acampamento");
    for (let n = 0; n < target; n++) {
      let placed: { x: number; z: number } | undefined;
      for (const spacing of [600]) {
        placed = best(
          draws(r, 1200),
          (c) => {
            const b = g.biomeAt(c.x, c.z);
            const d = arrivalPoi ? dist(c, arrivalPoi) : 350;
            // O 1º a 200–500 m da chegada (doc 04: um mascate logo no começo); o 2º só ≥ 200 m dela,
            // porque as duas regras juntas raramente cabem com a chegada na praia.
            const near = n === 0 ? d >= 200 && d <= 500 : d >= 200;
            return (b === BIOME.mata || b === BIOME.cerrado) && g.isMainLand(c.x, c.z) && g.coast(c.x, c.z) > 60 && (treta || near) && nearest(c, of("acampamento")) >= spacing && g.flat(c.x, c.z, 22, TAN.d14, 1.5) && clear(c.x, c.z, footprintOf("acampamento"));
          },
          (c) => Math.min(800, nearest(c, of("acampamento"))) - (arrivalPoi ? Math.abs(dist(c, arrivalPoi) - 300) * 0.2 : 0),
        );
        if (placed) break;
      }
      if (!placed) break;
      add("acampamento", placed.x, placed.z, toUnitFloat(hash32(grids.seed, 31, n, sub)) * 6.283185307179586);
    }
  }

  // 7. Carrancas: uma a 120–200 m da chegada, uma a até 150 m dos destroços, o resto ≥ 220 m entre si.
  {
    const r = rng("carranca");
    const target = count("carranca");
    for (let n = 0; n < target; n++) {
      const placed = best(
        draws(r, 900),
        (c) => {
          if (!g.isMainLand(c.x, c.z) || !g.flat(c.x, c.z, 6, TAN.d14, 1) || !clear(c.x, c.z, footprintOf("carranca"))) return false;
          if (n === 0 && arrivalPoi) return dist(c, arrivalPoi) >= 120 && dist(c, arrivalPoi) <= 200;
          if (n === 1 && wreckPoi) return dist(c, wreckPoi) <= 150;
          return nearest(c, of("carranca")) >= 220;
        },
        (c) => Math.min(500, nearest(c, of("carranca"))),
      );
      if (placed) add("carranca", placed.x, placed.z, toUnitFloat(hash32(grids.seed, 41, n, sub)) * 6.283185307179586);
    }
  }

  // 8. Pedras de Encanto: fora de praia e mangue, plano em 10 m, ≥ 150 m entre si; 25 % de chefe.
  {
    const r = rng("pedra");
    const target = count("pedra");
    for (let n = 0; n < target; n++) {
      const placed = best(
        draws(r, 700),
        (c) => {
          const b = g.biomeAt(c.x, c.z);
          return g.isMainLand(c.x, c.z) && b !== BIOME.praia && b !== BIOME.mangue && nearest(c, of("pedra")) >= 150 && g.flat(c.x, c.z, 10, TAN.d14, 1) && clear(c.x, c.z, footprintOf("pedra"));
        },
        (c) => Math.min(400, nearest(c, of("pedra"))),
      );
      if (!placed) break;
      add("pedra", placed.x, placed.z, toUnitFloat(hash32(grids.seed, 51, n, sub)) * 6.283185307179586, toUnitFloat(hash32(grids.seed, 52, n, sub)) < 0.25 ? 1 : 0);
    }
  }

  // 9. Ruínas de engenho: mata ou cerrado, plano em 15 m, ≥ 300 m entre si; giro de 90° em 90°.
  {
    const r = rng("ruina");
    const target = count("ruina");
    for (let n = 0; n < target; n++) {
      const placed = best(
        draws(r, 900),
        (c) => {
          const b = g.biomeAt(c.x, c.z);
          return (b === BIOME.mata || b === BIOME.cerrado) && g.isMainLand(c.x, c.z) && nearest(c, of("ruina")) >= 300 && g.flat(c.x, c.z, 15, TAN.d14, 1) && clear(c.x, c.z, footprintOf("ruina"));
        },
        (c) => Math.min(600, nearest(c, of("ruina"))),
      );
      if (!placed) break;
      add("ruina", placed.x, placed.z, Math.floor(toUnitFloat(hash32(grids.seed, 61, n, sub)) * 4) * 1.5707963267948966);
    }
  }

  // 10. Baús de Simpatia avulsos: terra com declive < 25°, ≥ 45 m entre si e das pegadas.
  {
    const r = rng("simpatia");
    const target = count("simpatia");
    const kinds = Object.values(SIMPATIA_CHESTS);
    for (let n = 0; n < target; n++) {
      const placed = best(
        draws(r, 120),
        (c) => g.isMainLand(c.x, c.z) && g.slopeAt(c.x, c.z) < TAN.d25 && nearest(c, of("simpatia")) >= 45 && clear(c.x, c.z, footprintOf("simpatia") + 36),
        (c) => Math.min(200, nearest(c, of("simpatia"))),
      );
      if (!placed) continue;
      let w = toUnitFloat(hash32(grids.seed, 71, n, sub));
      let kind = kinds.length - 1;
      for (let k = 0; k < kinds.length; k++) {
        const spawn = (kinds[k] as { spawn: number }).spawn;
        if (w < spawn) {
          kind = k;
          break;
        }
        w -= spawn;
      }
      add("simpatia", placed.x, placed.z, toUnitFloat(hash32(grids.seed, 72, n, sub)) * 6.283185307179586, kind);
    }
  }

  // Mínimos de todas as regras: obrigatório faltando invalida a ilha; os outros pedem outra sub-seed.
  const short: string[] = [];
  for (const rule of POI_RULES) {
    if (of(rule.type).length >= minOf(rule.type) || missing.some((m) => m.startsWith(rule.type))) continue;
    (rule.required ? missing : short).push(rule.type);
  }
  return { pois, missing, short, heading };
}

const footprintOf = (type: PoiType): number => POI_RULE.get(type)?.footprint ?? 0;

/** Altura-alvo do carimbo: média da altura base no raio plano (nunca abaixo do mar). */
function stampTarget(field: TerrainField, x: number, z: number, inner: number): number {
  let sum = field.unstampedHeight(x, z);
  const r = inner * 0.6;
  for (let k = 0; k < 8; k++) sum += field.unstampedHeight(x + dsin(k * 0.7853981633974483) * r, z + dcos(k * 0.7853981633974483) * r);
  return Math.max(0.6, sum / 9);
}

/** Acesso às grades do plano com consultas por raio. */
class Grids {
  readonly heights: Float32Array;
  readonly slope: Float32Array;
  readonly biome: Uint8Array;
  readonly mainLand: Uint8Array;
  readonly coastDistance: Float32Array;

  constructor(g: PoiGrids) {
    this.heights = g.heights;
    this.slope = g.slope;
    this.biome = g.biome;
    this.mainLand = g.mainLand;
    this.coastDistance = g.coastDistance;
  }

  private index(x: number, z: number): number {
    return cellOf(z) * PLAN_SIDE + cellOf(x);
  }

  height(x: number, z: number): number {
    return this.heights[this.index(x, z)] as number;
  }

  slopeAt(x: number, z: number): number {
    return this.slope[this.index(x, z)] as number;
  }

  biomeAt(x: number, z: number): number {
    return this.biome[this.index(x, z)] as number;
  }

  coast(x: number, z: number): number {
    return this.coastDistance[this.index(x, z)] as number;
  }

  isLand(x: number, z: number): boolean {
    return this.height(x, z) >= 1;
  }

  isMainLand(x: number, z: number): boolean {
    const i = this.index(x, z);
    return this.mainLand[i] === 1 && (this.heights[i] as number) >= 1;
  }

  /** Todas as células do raio em terra, com declive até `maxSlope` (o carimbo aplaina o resto). */
  flat(x: number, z: number, radius: number, maxSlope: number, minHeight: number): boolean {
    const i0 = cellOf(x - radius);
    const i1 = cellOf(x + radius);
    const j0 = cellOf(z - radius);
    const j1 = cellOf(z + radius);
    const r2 = (radius + PLAN_SPACING * 0.5) * (radius + PLAN_SPACING * 0.5);
    for (let j = j0; j <= j1; j++) {
      for (let i = i0; i <= i1; i++) {
        const dx = coord(i) - x;
        const dz = coord(j) - z;
        if (dx * dx + dz * dz > r2) continue;
        const k = j * PLAN_SIDE + i;
        if ((this.heights[k] as number) < minHeight || (this.slope[k] as number) > maxSlope) return false;
      }
    }
    return true;
  }

  /** Mar aberto: de 30 m a `reach` m na direção de fora (do centro de massa para o ponto), só água. */
  openSea(x: number, z: number, center: { x: number; z: number }, reach: number): boolean {
    const dx = x - center.x;
    const dz = z - center.z;
    const d = Math.sqrt(dx * dx + dz * dz) || 1;
    for (let s = 30; s <= reach; s += 15) {
      const px = x + (dx / d) * s;
      const pz = z + (dz / d) * s;
      if (Math.abs(px) >= WORLD_HALF || Math.abs(pz) >= WORLD_HALF) return true;
      if (this.height(px, pz) >= 0) return false;
    }
    return true;
  }

  /** Direção de descida (gradiente da altura do plano, normalizado). */
  downhill(x: number, z: number): { x: number; z: number } {
    const hx = this.height(x + PLAN_SPACING, z) - this.height(x - PLAN_SPACING, z);
    const hz = this.height(x, z + PLAN_SPACING) - this.height(x, z - PLAN_SPACING);
    const d = Math.sqrt(hx * hx + hz * hz) || 1;
    return { x: -hx / d, z: -hz / d };
  }

  /** Centro de massa da terra. */
  centroid(): { x: number; z: number } {
    let sx = 0;
    let sz = 0;
    let n = 0;
    for (let j = 0; j < PLAN_SIDE; j++) {
      for (let i = 0; i < PLAN_SIDE; i++) {
        if ((this.heights[j * PLAN_SIDE + i] as number) < 0) continue;
        sx += coord(i);
        sz += coord(j);
        n++;
      }
    }
    return n > 0 ? { x: sx / n, z: sz / n } : { x: 0, z: 0 };
  }

  /** Células que passam no filtro, como candidatos (centro da célula). */
  *cells(keep: (i: number) => boolean): Generator<{ x: number; z: number; i: number }> {
    for (let j = 0; j < PLAN_SIDE; j++) {
      for (let i = 0; i < PLAN_SIDE; i++) {
        const k = j * PLAN_SIDE + i;
        if (keep(k)) yield { x: coord(i), z: coord(j), i: k };
      }
    }
  }
}

/** Carimbos de terreno dos POIs (os que têm raio). */
export function poiStamps(pois: readonly Poi[]): { x: number; z: number; inner: number; outer: number; target: number }[] {
  return pois.filter((p) => p.outer > 0).map((p) => ({ x: p.x, z: p.z, inner: p.inner, outer: p.outer, target: p.target }));
}

